import { Extension } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

const templateSlotKey = new PluginKey<DecorationSet>('templateSlotPreview');
// Keep this byte-for-byte aligned with the render-side matcher.
// oxlint-disable-next-line eslint/no-useless-escape
const templateSlotPattern = /\[\[[^\[\]\r\n]+\]\]/g;

const isCode = (node: ProseMirrorNode, parent: ProseMirrorNode | null): boolean =>
  parent?.type.name === 'codeBlock' || node.marks.some((mark) => mark.type.name === 'code');

/**
 * Preview complete template-slot tokens that live in one text node.
 *
 * The compact span is a view decoration; selecting its text expands it for editing. Neither
 * state adds a mark, node, or attribute to saved HTML. A token split by markup is intentionally
 * left alone because joining text across nodes would span unrelated inline elements.
 */
const buildTemplateSlotDecorations = (
  doc: ProseMirrorNode,
  selection: { from: number; to: number }
): DecorationSet => {
  const decorations: Decoration[] = [];
  doc.descendants((node, pos, parent) => {
    if (!node.isText || !node.text || isCode(node, parent)) return;

    for (const match of node.text.matchAll(templateSlotPattern)) {
      if (match.index === undefined) continue;
      const from = pos + match.index;
      const to = from + match[0].length;
      const active = selection.from <= to && selection.to >= from;
      decorations.push(
        Decoration.inline(from, to, {
          'data-template-slot': 'true',
          title: match[0],
          style: active
            ? 'overflow-wrap: anywhere; white-space: normal;'
            : 'display: inline-block; max-width: 10ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: bottom;'
        })
      );
    }
  });
  return DecorationSet.create(doc, decorations);
};

/** Compacts idle [[template slots]] without changing stored document HTML. */
export const TemplateSlotPreview = Extension.create<{ compact: boolean }>({
  name: 'templateSlotPreview',

  addOptions() {
    return { compact: true };
  },

  addProseMirrorPlugins() {
    if (!this.options.compact) return [];
    return [
      new Plugin({
        key: templateSlotKey,
        state: {
          init: (_, state) => buildTemplateSlotDecorations(state.doc, state.selection),
          apply(tr, decorations) {
            return tr.docChanged || tr.selectionSet
              ? buildTemplateSlotDecorations(tr.doc, tr.selection)
              : decorations;
          }
        },
        props: {
          decorations(state) {
            return templateSlotKey.getState(state);
          }
        }
      })
    ];
  }
});
